import React, { Children } from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { FiretreeEmblem } from '../components/FiretreeEmblem';
import { PageBanner } from '../components/PageBanner';
export function HowWeWorkPage() {
  return <div data-id="element-529">
      <PageBanner title="How We Work" image="https://images.unsplash.com/photo-1528181304800-259b08848526?q=80&w=2070&auto=format&fit=crop" data-id="element-530" />

      {/* Intro Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-531">
        <div className="max-w-3xl mx-auto" data-id="element-532">
          <AnimatedSection delayClass="delay-200" data-id="element-533">
            <p className="text-xl md:text-2xl text-brand-brown leading-relaxed" data-id="element-534">
              Most funders come to this work from the outside. Firetree's team
              members have spent decades founding, running and governing
              nonprofit organisations across Southeast Asia — simultaneously
              alongside the funding work.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-535">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-536">
              This is what we mean by practitioner-led funding. We assess
              organisations not against an arbitrary rubric in isolation, but
              against direct experience of doing the same work ourselves. We
              know what good looks like because we have had to build it, defend
              it, and sometimes rebuild it. Over more than 20 years and $100M
              deployed, that practitioner judgment has shaped every partnership
              we have formed and every decision we have made about where
              resources can do the most good.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 1 — Two images side by side */}
      <div className="w-full py-8" data-id="element-537">
        <AnimatedSection data-id="element-538">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-id="element-539">
            <div className="overflow-hidden" data-id="element-540">
              <img src="https://cdn.magicpatterns.com/uploads/svuqCvjzYKpdsqKhxyHAry/DSCN3897.jpg" alt="Children smiling in a Southeast Asian community" className="w-full h-64 md:h-80 object-cover" data-id="element-541" />
            </div>
            <div className="overflow-hidden" data-id="element-542">
              <img src="https://cdn.magicpatterns.com/uploads/pFKH3vwEguyebNTbFmxX1T/P1140460.jpg" alt="Children in a Southeast Asian community" className="w-full h-64 md:h-80 object-cover" data-id="element-543" />
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We operate, not just fund */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-544">
        <div className="max-w-3xl mx-auto" data-id="element-545">
          <AnimatedSection data-id="element-546">
            <FiretreeEmblem className="text-brand-red opacity-30 mb-4" size={22} data-id="element-547" />
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-548">
              We operate, not just fund
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-549">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-550">
              Tondo Community Initiative in Manila is our child protection
              initiative, serving one of the Philippines' most marginalised
              urban communities. Starfish Education Foundation in Thailand is
              our education innovation initiative, working directly with public
              schools, teachers and government agencies. We fully fund and run
              both.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-551">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-552">
              This matters because it keeps us grounded. We know what rigorous
              child protection looks like from the inside. We know what
              governance under pressure feels like. We know what it costs — in
              time, talent and resources — to deliver quality work at the
              community level. When we assess a partner organisation, we are not
              working from a framework. We are drawing on direct experiences of
              doing the same work ourselves.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 2 — Single wide image */}
      <div className="w-full py-8" data-id="element-553">
        <AnimatedSection data-id="element-554">
          <div className="overflow-hidden" data-id="element-555">
            <img src="https://cdn.magicpatterns.com/uploads/mkZE6qihPVdec631fiWWkN/FromTerri_Cambodia6.jpg" alt="Rice fields in Cambodia" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-556" />
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We've been in the room */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-557">
        <div className="max-w-3xl mx-auto" data-id="element-558">
          <AnimatedSection data-id="element-559">
            <FiretreeEmblem className="text-brand-red opacity-30 mb-4" size={22} data-id="element-560" />
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-561">
              We've been in the room
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-562">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-563">
              Firetree's principal has spent decades making the same kinds of
              decisions the leaders we fund are making now — about programme
              pivots, team management under pressure, and moments where the
              right path forward is genuinely unclear. When we sit with a
              partner, we are not offering an outside perspective. We know what
              it feels like to hold an organisation together through difficulty,
              to balance what a community needs against what the resources
              allow, and to manage the gap between what looks good on paper and
              what is actually happening on the ground.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-564">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-565">
              This is what it means to work as peers. Board experience across
              more than 20 organisations in Southeast Asia deepens that; it
              means we have seen how leadership teams behave when things are
              hard, not just when things are going well. But what positions us
              as peers is simpler than that: we have been and are in those
              shoes, and we bring that into every conversation we have with a
              partner.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 3 — Asymmetric two-up */}
      <div className="w-full py-8" data-id="element-566">
        <AnimatedSection data-id="element-567">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2" data-id="element-568">
            <div className="md:col-span-3 overflow-hidden" data-id="element-569">
              <img src="https://cdn.magicpatterns.com/uploads/oycjRBLuBGVUMSr6Xgqnpb/FromTerri_Cambodia2.jpg" alt="Lush green waterway in Cambodia" className="w-full h-64 md:h-96 object-cover" data-id="element-570" />
            </div>
            <div className="md:col-span-2 overflow-hidden" data-id="element-571">
              <img src="https://cdn.magicpatterns.com/uploads/svuqCvjzYKpdsqKhxyHAry/DSCN3897.jpg" alt="Community life in Southeast Asia" className="w-full h-64 md:h-96 object-cover object-left" data-id="element-572" />
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We stay */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-573">
        <div className="max-w-3xl mx-auto" data-id="element-574">
          <AnimatedSection data-id="element-575">
            <FiretreeEmblem className="text-brand-red opacity-30 mb-4" size={22} data-id="element-576" />
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-577">
              We stay
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-578">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-579">
              Before we fund anyone, we spend significant time with the
              leadership, the team, and the communities they serve. We watch how
              decisions get made and how the organisation behaves when things
              are hard. We provide initial grant(s) specifically to create the
              time and space for that understanding to develop on both sides. By
              the time an organisation enters our Fund portfolio, we know it the
              way a colleague would.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-580">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-581">
              Our funding relationships span multiple years. Short funding
              cycles consume the energy that leaders should be spending on their
              work. When an organisation knows its funding relationship is
              stable, it can plan properly, hire the right people, adapt and
              evolve as they see the need to and do all of this in a timely
              manner, without the distraction of worrying about where the next
              resources are coming from. Leaders who trust that a funder will
              not exit at the first sign of difficulty are more likely to share
              the real challenges they face. That openness is what allows us to
              be genuinely useful not just as a source of funding, but as a
              thought partner when situations are complicated.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-582">
            <p className="text-xl text-brand-charcoal leading-relaxed mt-8 font-semibold font-serif" data-id="element-583">
              We have worked with some of our partners for over 15 years.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 4 — Full width */}
      <div className="w-full py-8" data-id="element-584">
        <AnimatedSection data-id="element-585">
          <div className="overflow-hidden" data-id="element-586">
            <img src="https://cdn.magicpatterns.com/uploads/pFKH3vwEguyebNTbFmxX1T/P1140460.jpg" alt="Children in a Southeast Asian community" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-587" />
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We share who we back */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-588">
        <div className="max-w-3xl mx-auto" data-id="element-589">
          <AnimatedSection data-id="element-590">
            <FiretreeEmblem className="text-brand-red opacity-30 mb-4" size={22} data-id="element-591" />
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-592">
              We share who we back
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-593">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-594">
              Every organisation in the Firetree Fund portfolio is one we
              currently fund ourselves — and intend to keep funding. These are
              not recent discoveries. They represent some of the most effective
              and operationally mature organisations in their field, and they
              have been through years of real partnership with us before being
              introduced to fellow funders.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-595">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-596">
              Organisations with demonstrated impact, strong leadership and
              established accountability mechanisms are considered for inclusion
              in the Firetree Fund portfolio. Before reaching this point, each
              has been through rigorous validation with us — assessed for
              financial health, leadership resilience under pressure, learning
              agility and a proven ability to deploy resources effectively.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-597">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-598">
              We believe successful organisations should not be heavily
              dependent on a single or very few donors. The organisations we
              bring to the Fund maintain diversified funding bases — we would
              fund them more ourselves if that principle didn't hold us back.
              They are ready for more, and we know it.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-400" data-id="element-599">
            <div className="mt-10 p-8 bg-brand-card/30 rounded-2xl border border-brand-shape/50" data-id="element-600">
              <p className="text-lg text-brand-charcoal leading-relaxed font-medium italic" data-id="element-601">
                This is the offer the Firetree Fund makes: not a directory, but
                curated access to organisations tested through real partnership
                and assessed by people who are practitioners and funders
                themselves.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}